// GenericSwiper.jsx
import React, { useRef, useState, useEffect, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { Box, Typography, Button, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import "@styles/components/ui/GenericSwiper.scss";
import { addClickRate } from "@api/landingApi";
import VideoPlayer from "@components/ui/VideoPlayer";
import { Trans } from "react-i18next";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { gaEvent } from "@utils/gaEvent";

const GenericSwiper = ({
  slides,
  type,
  onVideoOpen, // ← 新增
  onVideoClose, // ← 新增
  renderPopupExtras, // ← 新增: (activeVideo) => ReactNode
}) => {
  const swiperRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [activeId, setActiveId] = useState(slides?.[0]?.id ?? null);
  const [activeVideo, setActiveVideo] = useState(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");

  const useLoop = slides.length > 3;
  const useCentered = slides.length > 3;

  const initialIndex = useMemo(() => {
    const idx = slides.findIndex((s) => s.id === activeId);
    return idx >= 0 ? idx : 0;
  }, [slides, activeId]);

  const swiperKey = `${useLoop}-${useCentered}-${slides.length}`;

  useEffect(() => {
    if (!activeId && slides.length > 0) {
      setActiveId(slides[0].id);
    }
  }, [slides, activeId]);

  const postClickRate = async ({ id, article_type }) => {
    try {
      const res = await addClickRate({ id, article_type });
      console.log("Click tracked:", res.data.StatusCode);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCardClick = (slide) => {
    if (type === "event") {
      navigate(`/event_form?id=${slide.id}`);
      return;
    }

    if (type === "blog") {
      gaEvent("clubs_homepage", "blog", "blog_click", slide.id);
      navigate(`/blog_article?blog_id=${slide.id}`);
      return;
    }

    if (type === "video") {
      gaEvent("clubs_homepage", "video", "video_click", slide.id);
      postClickRate({ id: slide.id, article_type: type });
      setActiveVideo(slide); // 需要 slide.url
      onVideoOpen?.(slide); // ← 通知 Parent 拉「相關 BLOG」
      return;
    }
  };

  const closePopup = () => {
    setActiveVideo(null);
    onVideoClose?.(); // ← 讓 Parent 可清理狀態
  };


  return (
    <Box
      className={`generic-swiper-wrapper ${
        slides.length <= 3 ? "no-scale" : ""
      }`}
    >
      {/* 導航鍵（<=3 隱藏） */}
      <Box
        ref={prevRef}
        className={`custom-swiper-button-prev ${
          slides.length <= 3 ? "no-display-swiper-button" : ""
        }`}
      >
        <ArrowBackIosNewIcon />
      </Box>
      <Box
        ref={nextRef}
        className={`custom-swiper-button-next ${
          slides.length <= 3 ? "no-display-swiper-button" : ""
        }`}
      >
        <ArrowForwardIosIcon />
      </Box>

      <Swiper
        key={swiperKey}
        modules={[Autoplay, Navigation]}
        slidesPerView={!isMobile ? 3 : "auto"}
        spaceBetween={0}
        centeredSlides={useCentered}
        loop={useLoop}
        allowTouchMove={true}
        speed={800}
        centeredSlidesBounds
        centerInsufficientSlides
        initialSlide={initialIndex}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          const idx = useLoop ? swiper.realIndex : swiper.activeIndex;
          const s = slides[idx];
          if (s) setActiveId(s.id);

          setTimeout(() => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
            swiper.navigation.init();
            swiper.navigation.update();

            if (useLoop) swiper.slideToLoop(initialIndex, 0, false);
            else swiper.slideTo(initialIndex, 0, false);
          });
        }}
        onSlideChange={(swiper) => {
          const idx = useLoop ? swiper.realIndex : swiper.activeIndex;
          const s = slides[idx];
          if (s) setActiveId(s.id);
        }}
        observer
        observeParents
        watchSlidesProgress
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.id}>
            <Box
              className="custom-image-wrapper"
              onClick={() => handleCardClick(slide)}
            >
              <Box className="card-image-container">
                {type === "video" && (
                  <Box className="video-player-icon-container">
                    <PlayArrowRoundedIcon className="video-player-icon" />
                  </Box>
                )}
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="custom-image"
                />
              </Box>

              <Box className="detail-container">
                <Box className="title-button-container">
                  <Typography className="detail-title">
                    {slide.title}
                  </Typography>
                </Box>
                <Typography className="description">
                  {slide.description}
                </Typography>
              </Box>
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* video popup */}
      {type === "video" && activeVideo && (
        <Box className="video-popup-overlay" onClick={closePopup}>
          <Box className="video-popup-outline">
            <Box
              className="video-popup-content"
              onClick={(e) => e.stopPropagation()}
            >
              <CloseIcon className="popup-close-button" onClick={closePopup} />
              <Box className="popup-video">
                <VideoPlayer videoUrl={activeVideo.url} />
              </Box>
              <Typography className="popup-title">
                {activeVideo.title}
              </Typography>
              <Box className="popup-description-container">
                <Typography className="popup-description">
                  {activeVideo.description}
                </Typography>
              </Box>

              {/* 讓 Parent 插槽式地塞「相關 BLOG」等內容 */}
              {typeof renderPopupExtras === "function" &&
                renderPopupExtras(activeVideo)}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GenericSwiper;
